import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, StatusBadge, MarginBadge, Folio, Field, NativeSelect, Empty } from "../components/common";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { money, fdate } from "../lib/format";

const STATUSES = ["draft", "sent", "accepted", "rejected", "cancelled"];

function NewQuoteDialog({ open, onClose }) {
  const { t } = useT();
  const nav = useNavigate();
  const { data: customers } = useFetch(open ? "/customers" : null, [open]);
  const [f, setF] = useState({ customer_id: "", currency: "", title: "" });
  const create = async () => {
    if (!f.customer_id) return toast.error(t("common.required") + ": " + t("q.customer"));
    try {
      const r = await api.post("/quotations", f);
      toast.success(r.data.folio);
      nav(`/quotations/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-quote-dialog">
        <DialogHeader><DialogTitle>{t("q.new")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label={t("q.customer")} required>
            <NativeSelect testid="new-quote-customer-select" value={f.customer_id} placeholder="—" onChange={(v) => setF({ ...f, customer_id: v })}
              options={(customers || []).map((c) => ({ value: c.id, label: `${c.name} (${c.country})` }))} />
          </Field>
          <Field label={t("q.currency")}>
            <NativeSelect testid="new-quote-currency-select" value={f.currency} placeholder="Auto" onChange={(v) => setF({ ...f, currency: v })}
              options={[{ value: "USD", label: "USD" }, { value: "MXN", label: "MXN" }]} />
          </Field>
          <Field label={t("q.subject")}>
            <Input data-testid="new-quote-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button data-testid="new-quote-create-btn" onClick={create}>{t("q.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Quotations() {
  const { t } = useT();
  const { can } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const url = `/quotations?${status ? `status=${status}&` : ""}${q ? `q=${encodeURIComponent(q)}` : ""}`;
  const { data } = useFetch(url);
  const showCost = can("cost:view");
  return (
    <div>
      <PageHeader title={t("q.title")} kicker={t("nav.group.ops")}>
        {can("quote:write") && <Button data-testid="create-quotation-btn" onClick={() => nav("/quotations/new")}><Plus className="h-4 w-4" />{t("q.new")}</Button>}
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Input data-testid="quotes-search-input" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} className="h-10 w-full rounded-full bg-white px-4 sm:max-w-xs" />
        <div className="flex flex-wrap gap-1.5" data-testid="quotes-status-filter">
          {["", ...STATUSES].map((s) => (
            <button key={s || "all"} data-testid={`quotes-filter-${s || "all"}`} onClick={() => setStatus(s)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${status === s ? "border-black bg-black text-white" : "border-neutral-300 bg-white text-neutral-600 hover:border-black hover:text-black"}`}>
              {s ? t(`status.${s}`) : t("common.all")}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-neutral-500" data-testid="quotes-count">{(data || []).length} {t("q.title").toLowerCase()}</span>
      </div>
      <div className="stagger space-y-3" data-testid="quotes-table">
        {(data || []).map((r) => (
          <Link key={r.id} to={`/quotations/${r.id}`} data-testid={`quote-row-${r.folio}`}
            className="group relative flex flex-wrap items-center gap-x-8 gap-y-3 overflow-hidden rounded-md border border-neutral-200 bg-white px-5 py-4 transition-[box-shadow,border-color] hover:border-neutral-300 hover:shadow-[0_6px_24px_rgba(0,0,0,0.07)]">
            <span className="absolute inset-y-0 left-0 w-1 bg-[#db0720] opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="flex min-w-[170px] flex-col gap-1.5">
              <span data-testid={`quote-link-${r.folio}`}><Folio>{r.folio}</Folio></span>
              <div className="flex items-center gap-1.5">
                <StatusBadge status={r.status} />
                <span className="rounded-sm bg-neutral-900 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">{r.current_rev == null ? t("q.norev") : `Rev ${r.current_rev}`}</span>
                {r.dirty && r.current_rev != null && <span title={t("q.unpublished")} className="h-2 w-2 rounded-full bg-amber-500" />}
              </div>
            </div>
            <div className="min-w-[220px] flex-1">
              <div className="text-base font-bold leading-tight text-black">{r.customer_name}</div>
              <div className="mt-0.5 line-clamp-1 text-sm text-neutral-600">{r.title || "—"}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-500">
                <span><b className="text-neutral-700">{r.country}</b></span><span>{fdate(r.quote_date)} → {fdate(r.valid_until)}</span>{r.sales_person && <span>{r.sales_person}</span>}
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{t("q.total")}</div>
                <div className="font-mono text-lg font-bold text-black">{money(r.totals?.total, r.currency)}</div>
                {!r.tax_definitive && <div className="flex items-center justify-end gap-1 text-[10px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" />{t("ws.taxNonDef")}</div>}
              </div>
              {showCost && <div className="text-right"><div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{t("q.margin")}</div><MarginBadge value={r.totals?.margin_pct} /></div>}
              <span data-testid={`quote-open-${r.folio}`} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold transition-colors group-hover:border-[#db0720] group-hover:bg-[#db0720] group-hover:text-white">
                <Pencil className="h-3.5 w-3.5" />{can("quote:write") ? t("common.edit") : t("common.open")}
              </span>
            </div>
          </Link>
        ))}
        {data && !data.length && <div className="rounded-md border border-dashed border-neutral-300 bg-white"><Empty>{t("common.empty")}</Empty></div>}
      </div>
    </div>
  );
}
